import React, { useEffect, useRef, useState } from 'react';
import { API } from '../../lib/api';
import { useLang } from '../../lib/lang';
import LogoSlashSVG from '../Intro/LogoSlashSVG';

const CHAR_MS = 28;
const REFLECTION_HOLD_MS = 2700;
const SESSION_KEY = 'araia_aphorism_sid';

const FALLBACK_REFLECTIONS_IT = [
    "OGNI SCELTA È UN PIENO CHE NASCONDE UN VUOTO.",
    "LA FORMA RICORDA. IL CONCETTO DIMENTICA.",
    "IL SILENZIO È GIÀ UNA RISPOSTA.",
    "OGNI STRUTTURA NASCE DA UN DUBBIO.",
];
const FALLBACK_REFLECTIONS_EN = [
    "EVERY CHOICE IS A FULLNESS HIDING AN EMPTINESS.",
    "FORM REMEMBERS. CONCEPT FORGETS.",
    "SILENCE IS ALREADY AN ANSWER.",
    "EVERY STRUCTURE BEGINS WITH A DOUBT.",
];

function _fallbackReflection(lang) {
    const list = lang === 'en' ? FALLBACK_REFLECTIONS_EN : FALLBACK_REFLECTIONS_IT;
    return list[Math.floor(Math.random() * list.length)];
}

function getSessionId() {
    try {
        let s = sessionStorage.getItem(SESSION_KEY);
        if (!s) {
            s = `aph-${Math.random().toString(36).slice(2)}-${Date.now()}`;
            sessionStorage.setItem(SESSION_KEY, s);
        }
        return s;
    } catch (e) {
        return 'anonymous';
    }
}

function useReducedMotion() {
    const [rm, setRm] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const on = () => setRm(mq.matches);
        on();
        mq.addEventListener?.('change', on);
        return () => mq.removeEventListener?.('change', on);
    }, []);
    return rm;
}

/**
 * Full-bleed generative chat that sits directly under the hero split on
 * the homepage. Not a chat window: each turn is its own big screen with
 * the question typed out huge and an editorial underline-only input.
 *
 * Flow:
 *   1. On first view → GET a seed question, typed out with the `//` cursor.
 *      Also stores the poles (pole_a / pole_b) tied to that seed.
 *   2. User types answer + Enter → POST /api/aphorism with the current
 *      seed_text and poles. Backend classifies as A / B / OFF or (if the
 *      seed has no poles) falls back to LLM generative reaction.
 *   3. A / B → direct transition to next question (no reaction).
 *   4. OFF   → show fallback message (~2s), then transition.
 *   5. LLM (no poles) → type reaction, hold, then transition.
 */
export default function AphorismChat() {
    const lang = useLang();
    const [question, setQuestion] = useState('');
    const [poleA, setPoleA] = useState('');
    const [poleB, setPoleB] = useState('');
    const [typed, setTyped] = useState('');
    const [phase, setPhase] = useState('typing'); // typing | ready | reacting | loading
    const [reaction, setReaction] = useState('');
    const [typedReaction, setTypedReaction] = useState('');
    const [reactionKind, setReactionKind] = useState('reaction'); // 'reaction' | 'off'
    const [answer, setAnswer] = useState('');
    const [history, setHistory] = useState([]);
    const [dismissed, setDismissed] = useState(false);

    const reduced = useReducedMotion();
    const sidRef = useRef(getSessionId());
    const inputRef = useRef(null);

    const applySeedFromResponse = (data) => {
        setPoleA(data?.pole_a || '');
        setPoleB(data?.pole_b || '');
        setQuestion(data?.next_question || '');
    };

    // --- Fetch first question on mount
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${API}/aphorism`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sidRef.current, history: [], lang }),
                });
                const data = await res.json();
                if (alive && data?.next_question) applySeedFromResponse(data);
            } catch (e) {
                if (alive) {
                    setQuestion(
                        lang === 'en'
                            ? 'DOES DESIGN AIM TO PLEASE OR TO DISTURB?'
                            : 'IL DESIGN SERVE A PIACERE O A DISTURBARE?',
                    );
                }
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- applySeedFromResponse only calls stable setters; recreating it per render is fine
    }, [lang]);

    // --- Type-out the current question when it changes
    useEffect(() => {
        if (!question) return;
        if (reduced) {
            setTyped(question);
            setPhase('ready');
            return;
        }
        setTyped('');
        setPhase('typing');
        let i = 0;
        const iv = setInterval(() => {
            i += 1;
            setTyped(question.slice(0, i));
            if (i >= question.length) {
                clearInterval(iv);
                setPhase('ready');
            }
        }, CHAR_MS);
        return () => clearInterval(iv);
    }, [question, reduced]);

    // Focus input SOLO quando la sezione entra effettivamente in viewport
    // (evita il "salto" allo scroll iniziale della home). Uso preventScroll
    // per non far comunque saltare la pagina sull'input.
    const sectionRef = useRef(null);
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting && phase === 'ready' && inputRef.current) {
                        try {
                            inputRef.current.focus({ preventScroll: true });
                        } catch (e) {
                            // preventScroll unsupported (older Safari) — bail
                            // rather than call focus() without the option, which
                            // would yank the viewport into the input.
                            console.debug('[AphorismChat] focus(preventScroll) unsupported:', e);
                        }
                    }
                }
            },
            { threshold: 0.6 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [phase]);

    // --- Send answer
    const submit = async (e) => {
        e?.preventDefault?.();
        const value = answer.trim();
        if (!value || phase === 'loading' || phase === 'reacting') return;

        const currentSeed = question;
        const currentA = poleA;
        const currentB = poleB;

        const nextHistory = [...history, { question: currentSeed, answer: value }];
        setHistory(nextHistory);
        setAnswer('');
        setPhase('loading');

        try {
            const res = await fetch(`${API}/aphorism`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: sidRef.current,
                    user_answer: value,
                    history: nextHistory,
                    current_seed_text: currentSeed,
                    current_pole_a: currentA,
                    current_pole_b: currentB,
                    lang,
                }),
            });
            const data = await res.json();

            const classification = data?.classification || null;
            const reflection =
                data?.reflection ||
                data?.message ||
                data?.reaction ||
                _fallbackReflection(lang);
            const nextQuestion =
                data?.next_question ||
                (lang === 'en'
                    ? 'CONTINUE. YOU HAVE NOT SURPRISED ME YET.'
                    : 'CONTINUA. NON MI HAI ANCORA STUPITO.');
            const nextPoleA = data?.pole_a || '';
            const nextPoleB = data?.pole_b || '';

            // Unified flow: EVERY answer resolves through the reflection
            // slide (A / B / OFF / unclassified). Reflection kind marks
            // classification for optional styling nuance.
            const kind = classification === 'OFF' ? 'off' : 'reflection';
            setReaction(reflection);
            setReactionKind(kind);
            setTypedReaction('');
            setPhase('reacting');

            const goNext = () => {
                setReaction('');
                setTypedReaction('');
                setReactionKind('reflection');
                setPoleA(nextPoleA);
                setPoleB(nextPoleB);
                setQuestion(nextQuestion);
            };

            if (reduced) {
                setTypedReaction(reflection);
                setTimeout(goNext, REFLECTION_HOLD_MS);
            } else {
                let i = 0;
                const iv = setInterval(() => {
                    i += 1;
                    setTypedReaction(reflection.slice(0, i));
                    if (i >= reflection.length) {
                        clearInterval(iv);
                        setTimeout(goNext, REFLECTION_HOLD_MS);
                    }
                }, CHAR_MS);
            }
        } catch (err) {
            // Silent fail — surface a fallback reflection then a fresh seed
            const reflection = _fallbackReflection(lang);
            const fallbackSeed =
                lang === 'en'
                    ? 'STRUCTURE OR DECONSTRUCTION?'
                    : 'STRUTTURA O DESTRUTTURAZIONE?';
            setReactionKind('reflection');
            setPhase('reacting');
            setReaction(reflection);
            setTypedReaction(reflection);
            setTimeout(() => {
                setReaction('');
                setTypedReaction('');
                setPoleA('');
                setPoleB('');
                setQuestion(fallbackSeed);
            }, REFLECTION_HOLD_MS);
        }
    };

    if (dismissed) return null;

    return (
        <section
            ref={sectionRef}
            className="aphorism-chat"
            data-testid="aphorism-chat"
            data-phase={phase}
            aria-label="Two Visions Ask"
        >
            <button
                type="button"
                className="aphorism-skip"
                data-testid="aphorism-skip"
                onClick={() => setDismissed(true)}
                aria-label="Skip chat"
            >
                SKIP
            </button>

            <div className="aphorism-inner">
                <span className="aphorism-eyebrow" aria-hidden="true">
                    <LogoSlashSVG barClassName="aphorism-eyebrow-bar" />
                    <span>TWO VISIONS · ASK</span>
                </span>

                {phase === 'reacting' ? (
                    <p
                        className={`aphorism-reaction aphorism-reflection${reactionKind === 'off' ? ' aphorism-reaction--off' : ''}`}
                        data-testid={reactionKind === 'off' ? 'aphorism-off-message' : 'aphorism-reflection'}
                        data-classification={reactionKind}
                    >
                        {typedReaction}
                        <span className="aphorism-slash-cursor" aria-hidden="true">
                            <LogoSlashSVG barClassName="aphorism-slash-bar" />
                        </span>
                    </p>
                ) : (
                    <h2
                        className="aphorism-question"
                        data-testid="aphorism-question"
                    >
                        {typed}
                        <span className="aphorism-slash-cursor" aria-hidden="true">
                            <LogoSlashSVG barClassName="aphorism-slash-bar" />
                        </span>
                    </h2>
                )}

                <form
                    className="aphorism-form"
                    onSubmit={submit}
                    data-testid="aphorism-form"
                >
                    <input
                        ref={inputRef}
                        type="text"
                        className="aphorism-input"
                        data-testid="aphorism-input"
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        placeholder={lang === 'en' ? 'Answer…' : 'Rispondi…'}
                        maxLength={200}
                        autoComplete="off"
                        spellCheck={false}
                        disabled={phase === 'loading' || phase === 'reacting'}
                        aria-label="Your answer"
                    />
                    <button
                        type="submit"
                        className="aphorism-submit"
                        data-testid="aphorism-submit"
                        disabled={
                            !answer.trim() ||
                            phase === 'loading' ||
                            phase === 'reacting'
                        }
                        aria-label="Send answer"
                    >
                        {phase === 'loading' ? '···' : '→'}
                    </button>
                </form>

                {history.length > 0 && (
                    <p className="aphorism-turn-count" aria-hidden="true">
                        TURN {String(history.length).padStart(2, '0')}
                    </p>
                )}
            </div>
        </section>
    );
}
